import React, { useEffect, useState } from 'react';
import { IAuditLogItemResponse } from 'models/auditLog';
import { AuditLogEventName } from 'pages/AuditLogPage/AuditLogViewPage/styles';
import { Box, Stack, Typography } from '@mui/material';
import {
  ILegalEntityCustomField,
  ILegalEntityData,
} from 'components/organisms/LegalEntityDetails';
import { IOrgLegalEntityData } from 'components/organisms/OrgLegalEntityDetails';
import { ERouteLinks } from 'models/route';
import Grid from '@mui/material/Grid';
import { DetailsTemplate, EDetailsPageTypes } from 'components/templates/DetailsTemplate';
import { useNavigate } from 'react-router-dom';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import { useTranslations } from 'hooks/useTranslations';
import { EDictionaryTypes, IDictionary } from 'models/dictionary';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import { useLang } from 'models/langContext';
import {
  EDictionaryPrefixTypesUniqueId,
  findInDictionaryList,
  IParsedDictionary,
  parseDictionary,
} from 'helpers/dictionary';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { ECustomFieldType } from 'models/customField';
import { formatDate } from 'utils/date';
import { EAuditLogEvents, EAuditLogObjectTypes } from 'pages/AuditLogPage/helpers';
import { mergeDictionaries } from 'components/organisms/Dictionaries';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { ILegalEntityContactWithAssociation } from 'models/legalEntity';
import { useDictionary } from 'models/dictionaryContext';
import { useWeaponLicenceModule } from 'hooks/useWeaponLicenceModule';
import { StyledLegalLink } from 'components/atoms/StyledLegalLink';
import { ELegalEntityTypes } from 'constants/LegalEntityTypes';
import { EOrganisationCategory } from '../../../constants/OrganisationCategory';
import { ELegalStatus } from 'constants/LegalStatus';
import { useSystemFieldsConfiguration } from 'pages/SystemSettingsPage/SystemFieldsConfigurationPage/hooks';
import { EFieldsOptional } from 'pages/SystemSettingsPage/SystemFieldsConfigurationPage/helpers';

export enum ELEObjectType {
  LE_INDIVIDUAL = 'LE_INDIVIDUAL',
  LE_ORGANIZATION = 'LE_ORGANIZATION',
}

interface ILegalEntitiesEventDetails {
  auditLog: IAuditLogItemResponse;
}

const LegalEntitiesEventDetails = ({ auditLog }: ILegalEntitiesEventDetails) => {
  const [legalEntity, setLegalEntity] = useState<ILegalEntityData | null>();
  const [previousLegalEntity, setPreviousLegalEntity] = useState<ILegalEntityData | null>();
  const [orgLegalEntity, setOrgLegalEntity] = useState<IOrgLegalEntityData | null>();
  const [previousOrgLegalEntity, setPreviousOrgLegalEntity] =
    useState<IOrgLegalEntityData | null>();
  const [legalEntityContact, setLegalEntityContact] =
    useState<ILegalEntityContactWithAssociation | null>();
  const [previousLegalEntityContact, setPreviousLegalEntityContact] =
    useState<ILegalEntityContactWithAssociation | null>();
  const navigate = useNavigate();
  const { t } = useTranslations();
  const { selectedLanguage, defaultLanguage } = useLang();
  const { shortDateFormat, unit, getProperties } = useGlobalProperty();
  const { hasWeaponLicenceModule } = useWeaponLicenceModule();
  const { getFieldsOptional, hiddenFieldsName } = useSystemFieldsConfiguration();

  const {
    loadDictionaries,
    gender,
    legalEntityPrefix,
    legalStatus,
    legalIndividualDeactivationReason,
    legalOrganisationDeactivationReason,
    civilianPrefix,
    militaryPrefix,
    policePrefix,
    legalVisibility,
    organisationStateType,
    organisationCivilianType,
    organisationCategory,
    organisationMilitaryType,
    organisationPoliceType,
    weaponLicencingAuthority,
    nationality,
    identificationDocumentType,
  } = useLegalEntities();

  const { phoneCode } = useDictionary();

  useEffect(() => {
    getProperties();
    getFieldsOptional();
  }, []);

  const parsedPhoneCodeDictionary: IParsedDictionary = parseDictionary(phoneCode, 'name');

  const isNotLegalContactEvent =
    auditLog?.eventNameUniqueId !== EAuditLogEvents.ADD_LE_INDIVIDUAL_CONTACT &&
    auditLog?.eventNameUniqueId !== EAuditLogEvents.EDIT_LE_INDIVIDUAL_CONTACT &&
    auditLog?.eventNameUniqueId !== EAuditLogEvents.DELETE_LE_INDIVIDUAL_CONTACT &&
    auditLog?.eventNameUniqueId !== EAuditLogEvents.ADD_LE_ORGANIZATION_CONTACT &&
    auditLog?.eventNameUniqueId !== EAuditLogEvents.EDIT_LE_ORGANIZATION_CONTACT &&
    auditLog?.eventNameUniqueId !== EAuditLogEvents.DELETE_LE_ORGANIZATION_CONTACT;

  const isNotLegalIndividualContactEvent =
    auditLog?.eventNameUniqueId !== EAuditLogEvents.ADD_LE_INDIVIDUAL_CONTACT &&
    auditLog?.eventNameUniqueId !== EAuditLogEvents.EDIT_LE_INDIVIDUAL_CONTACT &&
    auditLog?.eventNameUniqueId !== EAuditLogEvents.DELETE_LE_INDIVIDUAL_CONTACT;

  const dictionariesList =
    auditLog?.objectType === ELEObjectType.LE_INDIVIDUAL
      ? [
        EDictionaryTypes.Gender,
        EDictionaryTypes.LegalVisibility,
        EDictionaryTypes.legalEntityPrefix,
        EDictionaryTypes.legalStatus,
        EDictionaryTypes.legalIndividualDeactivationReason,
        EDictionaryTypes.civilianPrefix,
        EDictionaryTypes.militaryPrefix,
        EDictionaryTypes.policePrefix,
        EDictionaryTypes.weaponLicencingAuthority,
        EDictionaryTypes.nationality,
        EDictionaryTypes.IdentificationDocumentType,
        EDictionaryTypes.PhoneCode,
      ]
      : [
        EDictionaryTypes.LegalVisibility,
        EDictionaryTypes.OrganisationCivilianType,
        EDictionaryTypes.legalStatus,
        EDictionaryTypes.legalOrganisationDeactivationReason,
        EDictionaryTypes.OrganisationStateType,
        EDictionaryTypes.OrganisationCategory,
        EDictionaryTypes.weaponLicencingAuthority,
        EDictionaryTypes.nationality,
        EDictionaryTypes.PhoneCode,
      ];

  useEffect(() => {
    (async function init() {
      await loadDictionaries(dictionariesList);
    })();
  }, [selectedLanguage]);

  useEffect(() => {
    const fetchLegalEntity = async (): Promise<void> => {
      if (auditLog?.previousObjectValue) {
        const previousObjectValue = auditLog.previousObjectValue as any;
        if (isNotLegalContactEvent) {
          const formattedDate = formatDate(previousObjectValue?.dateOfBirth, shortDateFormat);
          const leIndividualData: ILegalEntityData = {
            ...previousObjectValue,
            dateOfBirth: formattedDate,
          };
          setPreviousLegalEntity(leIndividualData);
        } else {
          setPreviousLegalEntityContact(previousObjectValue);
        }
      }
    };

    if (auditLog?.postObjectValue) {
      if (isNotLegalContactEvent) {
        const postObjectValue = auditLog.postObjectValue as any;
        const formattedDate = formatDate(postObjectValue?.dateOfBirth, shortDateFormat);
        const updatedResponse = {
          ...postObjectValue,
          dateOfBirth: formattedDate,
        };
        if (auditLog.objectType === EAuditLogObjectTypes.LE_INDIVIDUAL) {
          setLegalEntity(updatedResponse);
        } else {
          setOrgLegalEntity(updatedResponse);
        }
      } else {
        const postObjectValue = auditLog.postObjectValue as any;
        setLegalEntityContact(postObjectValue);
      }
    }

    const fetchOrgLegalEntity = async (): Promise<void> => {
      if (auditLog?.previousObjectValue) {
        const previousObjectValue = auditLog.previousObjectValue as any;
        if (isNotLegalContactEvent) {
          setPreviousOrgLegalEntity(previousObjectValue);
        } else {
          setPreviousLegalEntityContact(previousObjectValue);
        }
      }
      if (auditLog?.postObjectValue) {
        const postObjectValue = auditLog.postObjectValue as any;
        if (isNotLegalContactEvent) {
          setOrgLegalEntity(postObjectValue);
        } else {
          setLegalEntityContact(postObjectValue);
        }
      }
    };

    if (auditLog?.objectType === ELEObjectType.LE_ORGANIZATION) fetchOrgLegalEntity();
    else if (auditLog?.objectType === ELEObjectType.LE_INDIVIDUAL) fetchLegalEntity();
  }, [
    auditLog,
    gender,
    legalVisibility,
    legalEntityPrefix,
    civilianPrefix,
    militaryPrefix,
    policePrefix,
    organisationStateType,
    organisationCivilianType,
  ]);

  const renderItemDetail = (
    dictionary: IDictionary[] | null,
    dictionaryItemId: string,
    le: ILegalEntityData | IOrgLegalEntityData,
  ) => dictionary?.find((item: IDictionary) => item?.uniqueId === le[dictionaryItemId])?.name;

  const renderPrefixDetail = (
    prefixGroupUniqueId: string | undefined,
    le: ILegalEntityData,
  ) => {
    switch (prefixGroupUniqueId) {
      case EDictionaryPrefixTypesUniqueId.civilianPrefix:
        return renderItemDetail(civilianPrefix, 'prefixValueUniqueId', le);
      case EDictionaryPrefixTypesUniqueId.militaryPrefix:
        return renderItemDetail(militaryPrefix, 'prefixValueUniqueId', le);
      case EDictionaryPrefixTypesUniqueId.policePrefix:
        return renderItemDetail(policePrefix, 'prefixValueUniqueId', le);
      default:
        return '';
    }
  };

  const renderCustomFieldData = (customField: ILegalEntityCustomField) => {
    if (customField.customFieldType === ECustomFieldType.BOOL) {
      if (customField.value === null) {
        return '-';
      }
      return customField.value
        ? t('customFields.value.yes.label')
        : t('customFields.value.no.label');
    }
    if (customField.customFieldType === ECustomFieldType.DATE) {
      return formatDate(customField.value, shortDateFormat);
    }
    return emptyFieldHolder(customField.value);
  };

  const isCivilian = (prefixGroupUniqueId: string | undefined) =>
    prefixGroupUniqueId === EDictionaryPrefixTypesUniqueId.civilianPrefix;

  const isStateOrganisationCategory = (organisationCategoryUniqueId: string | undefined) =>
    organisationCategoryUniqueId === EOrganisationCategory.state;

  const renderCustomFields = (le: ILegalEntityData | IOrgLegalEntityData) =>
    le.customFields?.map((customField) => (

      <Stack key={customField.customFieldUniqueId} sx={{ gridColumn: { xs: 'span 6', lg: 'span 3' } }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {customField?.translations?.filter(
            (translation) => selectedLanguage?.uniqueId === translation.languageUniqueId,
          )?.[0]?.name ||
            customField?.translations?.filter(
              (translation) => defaultLanguage?.uniqueId === translation.languageUniqueId,
            )?.[0]?.name}
        </Typography>
        <Typography variant="body2">{renderCustomFieldData(customField)}</Typography>
      </Stack>
    ));

  const renderFieldsForCivilian = (le: ILegalEntityData) => (
    <>
      {!hiddenFieldsName.includes(EFieldsOptional.placeOfBirth) && (

        <Grid size={{ xs: 6, lg: 3 }}>
          <Stack>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('legalEntityDetails.placeOfBirth.label')}
            </Typography>
            <Typography variant="body2">{le.placeOfBirth}</Typography>
          </Stack>
        </Grid>
      )}

      <Grid size={{ xs: 6, lg: 3 }}>
        <Stack>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t('legalEntityDetails.nationality.label')}
          </Typography>
          <Typography variant="body2">
            {le.nationalityUniqueId
              ? renderItemDetail(nationality, 'nationalityUniqueId', le)
              : '-'}
          </Typography>
        </Stack>
      </Grid>
      {!hiddenFieldsName.includes(EFieldsOptional.occupation) && (

        <Grid size={{ xs: 6, lg: 3 }}>
          <Stack>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('legalEntityDetails.occupation.label')}
            </Typography>
            <Typography variant="body2">{emptyFieldHolder(le.occupation)}</Typography>
          </Stack>
        </Grid>
      )}
      {!hiddenFieldsName.includes(EFieldsOptional.height) && (

        <Grid size={{ xs: 6, lg: 3 }}>
          <Stack>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('legalEntityDetails.height.label')}
            </Typography>
            <Typography variant="body2">{`${le.height} ${le.height ? unit : ''}`}</Typography>
          </Stack>
        </Grid>
      )}

      <Grid size={{ xs: 6, lg: 3 }}>
        <Stack>
          <Grid>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('legalEntityDetails.identityDocumentType.label')}
            </Typography>
          </Grid>
          <Typography variant="body2">
            {emptyFieldHolder(
              renderItemDetail(identificationDocumentType, 'identityDocumentTypeUniqueId', le),
            )}
          </Typography>
        </Stack>
      </Grid>

      <Grid size={{ xs: 6, lg: 3 }}>
        <Stack>
          <Grid>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('legalEntityDetails.identityDocumentNumber.label')}
            </Typography>
          </Grid>
          <Typography variant="body2">{emptyFieldHolder(le.identityDocumentNumber)}</Typography>
        </Stack>
      </Grid>
      {hasWeaponLicenceModule() && (
        <>

          <Grid size={{ xs: 6, lg: 3 }}>
            <Stack>
              <Grid>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('legalEntityDetails.primaryWeaponLicencingAuthorityUniqueId.label')}
                </Typography>
              </Grid>
              <Typography variant="body2">
                {emptyFieldHolder(
                  renderItemDetail(
                    weaponLicencingAuthority,
                    'primaryWeaponLicencingAuthorityUniqueId',
                    le,
                  ),
                )}
              </Typography>
            </Stack>
          </Grid>

          <Grid size={{ xs: 6, lg: 3 }}>
            <Stack>
              <Grid>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('legalEntityDetails.secondaryWeaponLicencingAuthorityUniqueId.label')}
                </Typography>
              </Grid>
              <Typography variant="body2">
                {emptyFieldHolder(
                  renderItemDetail(
                    weaponLicencingAuthority,
                    'secondaryWeaponLicencingAuthorityUniqueId',
                    le,
                  ),
                )}
              </Typography>
            </Stack>
          </Grid>
        </>
      )}
    </>
  );

  const renderFieldsForIndividualLegalEntity = (le: ILegalEntityData) => (
    <>

      <Grid size={{ xs: 6, lg: 3 }}>
        <Stack>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t('legalEntityDetails.legalEntityType.label')}
          </Typography>
          <Box>
            <Chips
              text={
                auditLog?.objectType === ELEObjectType.LE_INDIVIDUAL
                  ? t('legalEntityDetails.legalEntityType.individual.chip.label')
                  : t('legalEntityDetails.legalEntityType.organisation.chip.label')
              }
              variant={EChipsVariants.primary}
            />
          </Box>
        </Stack>
      </Grid>

      <Grid size={{ xs: 6, lg: 3 }}>
        <Stack>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t('legalEntities.individual.accordion.legalEntityTable.prefixGroup.column')}
          </Typography>
          <Typography variant="body2">
            <Chips
              text={renderItemDetail(legalEntityPrefix, 'prefixGroupUniqueId', le)}
              variant={EChipsVariants.primary}
            />
          </Typography>
        </Stack>
      </Grid>

      <Grid size={{ xs: 6, lg: 3 }}>
        <Stack>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t('legalEntityDetails.statusUniqueId.label')}
          </Typography>
          <Typography variant="body2">
            <Chips
              id="statusUniqueId"
              text={renderItemDetail(legalStatus, 'statusUniqueId', le)}
              variant={
                le.statusUniqueId === ELegalStatus.ACTIVE
                  ? EChipsVariants.success
                  : EChipsVariants.error
              }
            />
          </Typography>
        </Stack>
      </Grid>

      <Grid size={{ xs: 6, lg: 3 }}>
        <Stack>
          {le.statusUniqueId === ELegalStatus.INACTIVE && (
            <>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('legalEntityDetails.reasonOfDeactivation.label')}
              </Typography>
              <Typography variant="body2">
                {emptyFieldHolder(
                  renderItemDetail(
                    legalIndividualDeactivationReason,
                    'deactivationReasonUniqueId',
                    le,
                  ),
                )}
              </Typography>
            </>
          )}
        </Stack>
      </Grid>

      <Grid size={{ xs: 6, lg: 3 }}>
        <Stack>
          <Grid>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('legalEntityDetails.displayUniqueId.label')}
            </Typography>
          </Grid>
          <Typography variant="body2">{le.displayUniqueId}</Typography>
        </Stack>
      </Grid>

      <Grid size={{ xs: 6, lg: 3 }}>
        <Stack>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t('legalEntities.individual.accordion.legalEntityTable.prefixValue.column')}
          </Typography>
          <Typography variant="body2">
            {emptyFieldHolder(renderPrefixDetail(le.prefixGroupUniqueId, le))}
          </Typography>
        </Stack>
      </Grid>

      <Grid size={{ xs: 6, lg: 3 }}>
        <Stack>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t('legalEntityDetails.dateOfBirth.label')}
          </Typography>
          <Typography variant="body2">{le.dateOfBirth}</Typography>
        </Stack>
      </Grid>

      <Grid size={{ xs: 6, lg: 3 }}>
        <Stack>
          <Grid>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('legalEntityDetails.gender.label')}
            </Typography>
          </Grid>
          <Typography variant="body2">
            {renderItemDetail(gender, 'genderUniqueId', le)}
          </Typography>
        </Stack>
      </Grid>

      <Grid size={{ xs: 6, lg: 3 }}>
        <Stack>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t('legalEntityDetails.visibility.label')}
          </Typography>
          <Box>
            <Chips
              text={renderItemDetail(legalVisibility, 'visibilityUniqueId', le)}
              variant={EChipsVariants.success}
            />
          </Box>
        </Stack>
      </Grid>

      {isCivilian(le.prefixGroupUniqueId) && renderFieldsForCivilian(le)}
      {renderCustomFields(le)}
    </>
  );

  const renderFieldsForOrganisationLegalEntity = (le: IOrgLegalEntityData) => (
    <>

      <Grid size={{ xs: 6, lg: 3 }}>
        <Stack>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t('legalEntityDetails.legalEntityType.label')}
          </Typography>
          <Box>
            <Chips
              text={
                auditLog?.objectType === ELEObjectType.LE_INDIVIDUAL
                  ? t('legalEntityDetails.legalEntityType.individual.chip.label')
                  : t('legalEntityDetails.legalEntityType.organisation.chip.label')
              }
              variant={EChipsVariants.primary}
            />
          </Box>
        </Stack>
      </Grid>

      <Grid size={{ xs: 6, lg: 3 }}>
        <Stack>
          <Grid>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('legalEntityDetails.displayUniqueId.label')}
            </Typography>
          </Grid>
          <Typography variant="body2">{emptyFieldHolder(le.displayUniqueId)}</Typography>
        </Stack>
      </Grid>

      <Grid size={{ xs: 6, lg: 3 }}>
        <Stack>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t('legalEntityDetails.statusUniqueId.label')}
          </Typography>
          <Typography variant="body2">
            <Chips
              id="statusUniqueId"
              text={renderItemDetail(legalStatus, 'statusUniqueId', le)}
              variant={
                le.statusUniqueId === ELegalStatus.ACTIVE
                  ? EChipsVariants.success
                  : EChipsVariants.error
              }
            />
          </Typography>
        </Stack>
      </Grid>

      <Grid size={{ xs: 12, lg: 3 }}>
        <Stack>
          {le.statusUniqueId === ELegalStatus.INACTIVE && (
            <>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('legalEntityDetails.reasonOfDeactivation.label')}
              </Typography>
              <Typography variant="body2">
                {emptyFieldHolder(
                  renderItemDetail(
                    legalOrganisationDeactivationReason,
                    'deactivationReasonUniqueId',
                    le,
                  ),
                )}
              </Typography>
            </>
          )}
        </Stack>
      </Grid>

      <Grid size={{ xs: 6, lg: 3 }}>
        <Stack>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t('legalEntityDetails.name.label')}
          </Typography>
          <Typography variant="body2">{le.organizationName}</Typography>
        </Stack>
      </Grid>

      <Grid size={{ xs: 6, lg: 3 }}>
        <Stack>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t('legalEntityDetails.organisationCategory.label')}
          </Typography>
          <Typography variant="body2">
            {emptyFieldHolder(
              renderItemDetail(organisationCategory, 'organisationCategoryUniqueId', le),
            )}
          </Typography>
        </Stack>
      </Grid>

      <Grid size={{ xs: 6, lg: 3 }}>
        <Stack>
          <Grid>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('legalEntityDetails.organizationType.label')}
            </Typography>
          </Grid>
          <Typography variant="body2">
            {findInDictionaryList(
              le.organisationTypeUniqueIds,
              mergeDictionaries([
                organisationStateType,
                organisationCivilianType,
                organisationMilitaryType,
                organisationPoliceType,
              ]),
            )?.join(', ') || '-'}
          </Typography>
        </Stack>
      </Grid>
      {!hiddenFieldsName.includes(EFieldsOptional.departmentName) && (

        <Grid size={{ xs: 6, lg: 3 }}>
          <Stack>
            <Grid>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('legalEntityDetails.departmentName.label')}
              </Typography>
            </Grid>
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
              {emptyFieldHolder(le.departmentName)}
            </Typography>
          </Stack>
        </Grid>
      )}

      <Grid size={{ xs: 6, lg: 3 }}>
        <Stack>
          <Grid>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('legalEntityDetails.description.label')}
            </Typography>
          </Grid>
          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
            {emptyFieldHolder(le.description)}
          </Typography>
        </Stack>
      </Grid>

      <Grid size={{ xs: 6, lg: 3 }}>
        <Stack>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t('deleteLegalEntity.visibility.label')}
          </Typography>
          <Box>
            <Chips
              text={renderItemDetail(legalVisibility, 'visibilityUniqueId', le)}
              variant={EChipsVariants.success}
            />
          </Box>
        </Stack>
      </Grid>
      {!isStateOrganisationCategory(le.organisationCategoryUniqueId) &&
        hasWeaponLicenceModule() && (
          <>

            <Grid size={{ xs: 6, lg: 3 }}>
              <Stack>
                <Grid>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t('legalEntityDetails.primaryWeaponLicencingAuthorityUniqueId.label')}
                  </Typography>
                </Grid>
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  {emptyFieldHolder(
                    renderItemDetail(
                      weaponLicencingAuthority,
                      'primaryWeaponLicencingAuthorityUniqueId',
                      le,
                    ),
                  )}
                </Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 6, lg: 3 }}>
              <Stack>
                <Grid>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t('legalEntityDetails.secondaryWeaponLicencingAuthorityUniqueId.label')}
                  </Typography>
                </Grid>
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  {emptyFieldHolder(
                    renderItemDetail(
                      weaponLicencingAuthority,
                      'secondaryWeaponLicencingAuthorityUniqueId',
                      le,
                    ),
                  )}
                </Typography>
              </Stack>
            </Grid>
          </>
        )}
      {renderCustomFields(le)}
    </>
  );

  const renderFieldsForContactEvent = (le: ILegalEntityContactWithAssociation) =>
    isNotLegalIndividualContactEvent ? (
      <>

        <Grid size={{ xs: 6, lg: 3 }}>
          <Stack>
            <Grid>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('legalEntityDetails.contactTab.contactName.column')}
              </Typography>
            </Grid>
            <Typography variant="body2">
              <StyledLegalLink
                labelText={le.name}
                legalType={ELegalEntityTypes.individual}
                legalId={le.assignedIndividualUniqueId}
                legalVisibilityUniqueId={le.assignedIndividualVisibilityUniqueId}
              />
            </Typography>
          </Stack>
        </Grid>

        <Grid size={{ xs: 6, lg: 3 }}>
          <Stack>
            <Grid>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('legalEntityDetails.contactTab.role.column')}
              </Typography>
            </Grid>
            <Typography variant="body2">{emptyFieldHolder(le.role)}</Typography>
          </Stack>
        </Grid>

        <Grid size={{ xs: 6, lg: 3 }}>
          <Stack>
            <Grid>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('legalEntityDetails.contactTab.phoneNumber.column')}
              </Typography>
            </Grid>
            <Typography variant="body2">{`${le.phoneNumber &&
              parsedPhoneCodeDictionary?.[le.phoneNumber.codeDictionaryUniqueId]
              ? `${parsedPhoneCodeDictionary?.[le.phoneNumber.codeDictionaryUniqueId]}${le.phoneNumber.value
              }`
              : '-'
              }`}</Typography>
          </Stack>
        </Grid>

        <Grid size={{ xs: 6, lg: 3 }}>
          <Stack>
            <Grid>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('legalEntityDetails.contactTab.emailAddress.column')}
              </Typography>
            </Grid>
            <Typography variant="body2">{emptyFieldHolder(le.email)}</Typography>
          </Stack>
        </Grid>
      </>
    ) : (
      <>

        <Grid size={{ xs: 6, lg: 3 }}>
          <Stack>
            <Grid>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('legalEntityDetails.contactTab.phoneNumber.column')}
              </Typography>
            </Grid>
            <Typography variant="body2">{`${le.phoneNumber &&
              parsedPhoneCodeDictionary?.[le.phoneNumber.codeDictionaryUniqueId]
              ? `${parsedPhoneCodeDictionary?.[le.phoneNumber.codeDictionaryUniqueId]}${le.phoneNumber.value
              }`
              : '-'
              }`}</Typography>
          </Stack>
        </Grid>

        <Grid size={{ xs: 6, lg: 3 }}>
          <Stack>
            <Grid>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('legalEntityDetails.contactTab.emailAddress.column')}
              </Typography>
            </Grid>
            <Typography variant="body2">{emptyFieldHolder(le.email)}</Typography>
          </Stack>
        </Grid>
      </>
    );

  return isNotLegalContactEvent ? (
    <>
      {previousLegalEntity && (
        <>
          <AuditLogEventName>
            <Typography variant="h5">
              {auditLog?.eventNameUniqueId !== EAuditLogEvents.ADD_LE_INDIVIDUAL
                ? `${t('addAncillary.legalEntityDetails.breadcrumb')} ${t(
                  'auditLogDetails.before.event.title',
                )}`
                : `${t('addAncillary.legalEntityDetails.breadcrumb')} ${t(
                  'auditLogDetails.after.event.title',
                )}`}
            </Typography>
          </AuditLogEventName>
          <DetailsTemplate
            title={`${previousLegalEntity.firstName} ${previousLegalEntity.lastName}`}
            pageType={EDetailsPageTypes.auditLogView}
            disableActions
          >
            {renderFieldsForIndividualLegalEntity(previousLegalEntity)}
          </DetailsTemplate>
        </>
      )}

      {legalEntity && auditLog?.eventNameUniqueId !== EAuditLogEvents.DELETE_LE_INDIVIDUAL && (
        <AuditLogEventName sx={{ marginTop: '20px' }}>
          <Typography variant="h5">
            {`${t('addAncillary.legalEntityDetails.breadcrumb')} ${t(
              'auditLogDetails.post.event.title',
            )}`}
          </Typography>
        </AuditLogEventName>
      )}

      {legalEntity && (
        <DetailsTemplate
          title={`${legalEntity.firstName} ${legalEntity.lastName}`}
          pageType={EDetailsPageTypes.auditLogView}
          redirectToView={() =>
            navigate(
              `${ERouteLinks.ViewLegalEntity.replace(
                ':id',
                legalEntity.uniqueId,
              )}?type=individual`,
            )
          }
          disableActions={!legalEntity.uniqueId}
        >
          {renderFieldsForIndividualLegalEntity(legalEntity)}
        </DetailsTemplate>
      )}

      {previousOrgLegalEntity && (
        <>
          <AuditLogEventName>
            <Typography variant="h5">
              {auditLog?.eventNameUniqueId !== EAuditLogEvents.ADD_LE_ORGANIZATION
                ? `${t('addAncillary.legalEntityDetails.breadcrumb')} ${t(
                  'auditLogDetails.before.event.title',
                )}`
                : `${t('addAncillary.legalEntityDetails.breadcrumb')} ${t(
                  'auditLogDetails.after.event.title',
                )}`}
            </Typography>
          </AuditLogEventName>
          <DetailsTemplate
            title={`${previousOrgLegalEntity.organizationName}`}
            pageType={EDetailsPageTypes.auditLogView}
            disableActions
          >
            {renderFieldsForOrganisationLegalEntity(previousOrgLegalEntity)}
          </DetailsTemplate>
        </>
      )}

      {orgLegalEntity &&
        auditLog?.eventNameUniqueId !== EAuditLogEvents.DELETE_LE_ORGANIZATION && (
          <AuditLogEventName sx={{ marginTop: '20px' }}>
            <Typography variant="h5">
              {`${t('addAncillary.legalEntityDetails.breadcrumb')} ${t(
                'auditLogDetails.post.event.title',
              )}`}
            </Typography>
          </AuditLogEventName>
        )}

      {orgLegalEntity && (
        <DetailsTemplate
          title={`${orgLegalEntity.organizationName}`}
          pageType={EDetailsPageTypes.auditLogView}
          redirectToView={() =>
            navigate(
              `${ERouteLinks.ViewLegalEntity.replace(
                ':id',
                orgLegalEntity.uniqueId,
              )}?type=organisation`,
            )
          }
          disableActions={!orgLegalEntity.uniqueId}
        >
          {renderFieldsForOrganisationLegalEntity(orgLegalEntity)}
        </DetailsTemplate>
      )}
    </>
  ) : (
    <>
      {previousLegalEntityContact && (
        <>
          <AuditLogEventName>
            <Typography variant="h5">
              {auditLog?.eventNameUniqueId !== EAuditLogEvents.ADD_LE_INDIVIDUAL_CONTACT &&
                auditLog?.eventNameUniqueId !== EAuditLogEvents.ADD_LE_ORGANIZATION_CONTACT
                ? `${t('addAncillary.legalEntityDetails.breadcrumb')} ${t(
                  'auditLogDetails.before.event.title',
                )}`
                : `${t('addAncillary.legalEntityDetails.breadcrumb')} ${t(
                  'auditLogDetails.after.event.title',
                )}`}
            </Typography>
          </AuditLogEventName>
          <DetailsTemplate
            title={`${t('legalEntityDetails.contact.accordion.label')}`}
            pageType={EDetailsPageTypes.auditLogView}
            disableActions
          >
            {renderFieldsForContactEvent(previousLegalEntityContact)}
          </DetailsTemplate>
        </>
      )}

      {legalEntityContact &&
        auditLog?.eventNameUniqueId !== EAuditLogEvents.DELETE_LE_INDIVIDUAL_CONTACT &&
        auditLog?.eventNameUniqueId !== EAuditLogEvents.DELETE_LE_ORGANIZATION_CONTACT && (
          <AuditLogEventName sx={{ marginTop: '20px' }}>
            <Typography variant="h5">
              {`${t('addAncillary.legalEntityDetails.breadcrumb')} ${t(
                'auditLogDetails.post.event.title',
              )}`}
            </Typography>
          </AuditLogEventName>
        )}

      {legalEntityContact && (
        <DetailsTemplate
          title={`${t('legalEntityDetails.contact.accordion.label')}`}
          pageType={EDetailsPageTypes.auditLogView}
          redirectToView={() =>
            navigate(
              `${ERouteLinks.ViewLegalEntity.replace(':id', auditLog?.objectUniqueId)}?type=${isNotLegalIndividualContactEvent ? 'organisation' : 'individual'
              }&expand=contact`,
            )
          }
          disableActions={!auditLog?.objectUniqueId}
        >
          {renderFieldsForContactEvent(legalEntityContact)}
        </DetailsTemplate>
      )}
    </>
  );
};

export default LegalEntitiesEventDetails;
